import React from "react";
import { NavLink, useNavigate, Outlet, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { Home, Globe, Sparkles, BarChart3, User, Search, Sun, Moon, Coins, Flame, Plane, Crown } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useTheme } from "@/context/ThemeContext";

const tabs = [
  { to: "/app", icon: Home, label: "Home", end: true, id: "home" },
  { to: "/app/worlds", icon: Globe, label: "Worlds", id: "worlds" },
  { to: "/app/coach", icon: Sparkles, label: "Coach", id: "coach" },
  { to: "/app/premium", icon: Crown, label: "Premium", id: "premium" },
  { to: "/app/progress", icon: BarChart3, label: "Progress", id: "progress" },
  { to: "/app/profile", icon: User, label: "Profile", id: "profile" },
];

export default function Layout() {
  const { user } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();

  const level = user?.level || 1;

  return (
    <div className="min-h-screen aurora bg-background text-foreground">
      {/* Top bar */}
      <header className="sticky top-0 z-50 glass border-b border-border/50">
        <div className="max-w-6xl mx-auto px-4 h-16 flex items-center justify-between gap-3">
          <button
            data-testid="logo-home-btn"
            onClick={() => navigate("/app")}
            className="flex items-center gap-2 group"
          >
            <div className="h-9 w-9 rounded-xl bg-gradient-to-br from-indigo-500 to-violet-600 grid place-items-center shadow-lg shadow-indigo-500/30 transition-transform group-hover:-translate-y-0.5">
              <Plane size={18} className="text-white" />
            </div>
            <span className="font-display font-extrabold text-lg tracking-tight hidden sm:block">Lyfe<span className="text-indigo-400">Pilot</span></span>
          </button>

          <div className="flex items-center gap-2">
            <div data-testid="stat-level" className="hidden sm:flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-indigo-500/10 border border-indigo-500/25 text-indigo-300 text-sm font-semibold">
              <span className="text-[10px] uppercase tracking-widest opacity-70">Lv</span>{level}
            </div>
            <div data-testid="stat-coins" className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-yellow-500/10 border border-yellow-500/25 text-yellow-300 text-sm font-semibold">
              <Coins size={15} />{user?.coins ?? 0}
            </div>
            <div data-testid="stat-streak" className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-orange-500/10 border border-orange-500/25 text-orange-300 text-sm font-semibold">
              <Flame size={15} />{user?.streak ?? 0}
            </div>
            <button data-testid="search-btn" onClick={() => navigate("/app/search")} className="h-9 w-9 grid place-items-center rounded-full hover:bg-white/5 transition-colors">
              <Search size={18} />
            </button>
            <button data-testid="theme-toggle-btn" onClick={toggleTheme} className="h-9 w-9 grid place-items-center rounded-full hover:bg-white/5 transition-colors">
              {theme === "dark" ? <Sun size={18} /> : <Moon size={18} />}
            </button>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-6xl mx-auto px-4 pt-6 pb-28">
        <motion.div
          key={location.pathname}
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
        >
          <Outlet />
        </motion.div>
      </main>

      {/* Bottom nav */}
      <nav className="fixed bottom-0 inset-x-0 z-50 glass border-t border-border/50">
        <div className="max-w-6xl mx-auto px-2 grid grid-cols-6 h-[70px]">
          {tabs.map((t) => (
            <NavLink
              key={t.id}
              to={t.to}
              end={t.end}
              data-testid={`nav-${t.id}`}
              className={({ isActive }) =>
                `flex flex-col items-center justify-center gap-1 relative transition-colors ${
                  isActive ? "text-indigo-400" : "text-muted-foreground hover:text-foreground"
                }`
              }
            >
              {({ isActive }) => (
                <>
                  {isActive && (
                    <motion.span layoutId="navdot" className="absolute -top-0.5 h-1 w-8 rounded-full bg-indigo-400" />
                  )}
                  <t.icon size={22} strokeWidth={isActive ? 2.5 : 2} />
                  <span className="text-[10px] font-semibold">{t.label}</span>
                </>
              )}
            </NavLink>
          ))}
        </div>
      </nav>
    </div>
  );
}
